import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import { BrandingService } from "../../../foundation/altzero_base/base/services/brandingService";
import { OrganizationBranding } from "../../../foundation/altzero_base/base/types/organization";
import { convertContentToBlocks, convertBlocksToContent } from "../utils/documentUtils";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import PerfectMarkdownRenderer from "./common/PerfectMarkdownRenderer";





interface Section {
  id: string;
  title: string;
  content: string;
  regenerated?: boolean;
  fallback?: boolean;
  regeneratedAt?: string;
}



interface PDFLikePreviewProps {
  sections: Section[];
  documentTitle: string;
  clientData: {
    company: string;
    name?: string;
    email?: string;
  };
  projectData: {
    title: string;
    description?: string;
  };
  onSectionClick?: (sectionId: string) => void;
  selectedSectionId?: string;
  showSidebar?: boolean; // New prop to control sidebar visibility

}

// Ref interface for exposing methods to parent component
export interface PDFLikePreviewRef {
  scrollToSection: (sectionId: string) => void;
}

const PDFLikePreview = forwardRef<PDFLikePreviewRef, PDFLikePreviewProps>(({
  sections,
  documentTitle,
  clientData,
  projectData,
  onSectionClick,
  selectedSectionId,
  showSidebar = true, // Default to true for backward compatibility
}, ref) => {

  const { user } = useUser();
  const [organizationBranding, setOrganizationBranding] =
    useState<OrganizationBranding | null>(null);
  const [isLoadingBranding, setIsLoadingBranding] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Load organization branding (same pattern as DocumentsGenerated.tsx)
  const fetchOrganizationBranding = async () => {
    if (!user?.id) {
      console.log("❌ No user ID available for branding fetch");
      return;
    }

    setIsLoadingBranding(true);

    try {
      console.log("🔄 Fetching organization branding for user:", user.id);

      // Get user's organization ID from organisation_members table
      const { data: orgMember, error: orgError } = await supabase
        .from("organisation_members")
        .select("organisation_id")
        .eq("user_id", user.id)
        .single();

      if (orgError || !orgMember) {
        console.log("⚠️ No organization membership found for user");
        setIsLoadingBranding(false);
        return;
      }

      const organizationId = orgMember.organisation_id;
      console.log("🏢 Loading branding for organization:", organizationId);

      // Fetch organization branding
      const branding = await BrandingService.getOrganizationBranding(
        organizationId
      );

      if (branding) {
        console.log("✅ Organization branding loaded successfully:", branding);
        setOrganizationBranding(branding);
      } else {
        console.log("⚠️ No branding data found for organization");
      }
    } catch (error) {
      console.error("❌ Error fetching organization branding:", error);
    } finally {
      setIsLoadingBranding(false);
    }
  };

  // Load branding when component mounts
  useEffect(() => {
    if (user) {
      fetchOrganizationBranding();
    }
  }, [user]);

  // Calculate pages based on content length
  const pages = useMemo(() => {
    const WORDS_PER_PAGE = 500; // Increased for better content distribution
    const pagesData: Array<{ sections: Section[]; pageNumber: number }> = [];
    let currentPageSections: Section[] = [];
    let currentWordCount = 0;
    let pageNumber = 1;

    sections.forEach((section) => {
      const sectionWordCount = section.content.split(/\s+/).length;

      // If this section alone exceeds page limit, put it on its own page
      if (sectionWordCount > WORDS_PER_PAGE) {
        // If there are sections on current page, finish that page first
        if (currentPageSections.length > 0) {
          pagesData.push({
            sections: [...currentPageSections],
            pageNumber,
          });
          pageNumber++;
          currentPageSections = [];
          currentWordCount = 0;
        }

        // Put large section on its own page
        pagesData.push({
          sections: [section],
          pageNumber,
        });
        pageNumber++;
      } else if (
        currentWordCount + sectionWordCount > WORDS_PER_PAGE &&
        currentPageSections.length > 0
      ) {
        // Start new page if adding this section would exceed limit
        pagesData.push({
          sections: [...currentPageSections],
          pageNumber,
        });
        currentPageSections = [section];
        currentWordCount = sectionWordCount;
        pageNumber++;
      } else {
        // Add section to current page
        currentPageSections.push(section);
        currentWordCount += sectionWordCount;
      }
    });

    // Add the last page if it has content
    if (currentPageSections.length > 0) {
      pagesData.push({
        sections: currentPageSections,
        pageNumber,
      });
    }

    return pagesData.length > 0 ? pagesData : [{ sections: [], pageNumber: 1 }];
  }, [sections]);

  // Enhanced scroll to section functionality - brings section to TOP
  const scrollToSection = (sectionId: string) => {
    const sectionElement = sectionRefs.current[sectionId];
    if (sectionElement) {
      // Get the main content container
      const contentContainer = sectionElement.closest('.overflow-y-auto');

      if (contentContainer) {
        // Calculate the position to scroll to bring section to top
        const containerRect = contentContainer.getBoundingClientRect();
        const sectionRect = sectionElement.getBoundingClientRect();
        const scrollTop = contentContainer.scrollTop + (sectionRect.top - containerRect.top) - 20; // 20px offset from top

        // Smooth scroll to position
        contentContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      } else {
        // Fallback to standard scrollIntoView
        sectionElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    scrollToSection,
  }));

  const totalPages = pages.length;

  // Handle section click - scroll to section and update selection
  const handleSectionClick = (sectionId: string) => {
    // Use enhanced scroll function that brings section to TOP
    scrollToSection(sectionId);

    // Update selected section for highlighting
    if (onSectionClick) {
      onSectionClick(sectionId);
    }
  };

  // Get logo URL with cache busting
  const logoUrl = organizationBranding?.logo_url
    ? BrandingService.getLogoUrlWithCacheBusting(organizationBranding.logo_url)
    : null;

  const brandName = organizationBranding?.brand_title || "Your Company";
  const primaryColor = organizationBranding?.primary_color || "#3B82F6";

  return (
    <div className="flex h-full bg-gray-100">
      {/* Left Sidebar - Sections Navigation (conditionally rendered) */}
      {showSidebar && (
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium text-sm text-gray-900">
              Document Sections
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              {sections.length} sections
            </p>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="p-3">
              <div className="space-y-2">


                {/* Section Buttons */}
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionClick(section.id)}
                    className={`w-full text-left p-2 rounded-md text-sm transition-colors ${
                      selectedSectionId === section.id
                        ? "bg-blue-50 border border-blue-200 text-blue-900"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <div className="font-medium truncate">
                      {section.id === 'cover-page'
                        ? `📄 ${documentTitle}`
                        : `${index + 1}. ${section.title}`
                      }
                    </div>
                    <div className="text-xs text-gray-500 mt-1 truncate">
                      {section.id === 'cover-page'
                        ? 'Document cover page'
                        : `${section.content.substring(0, 60)}...`
                      }
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - PDF-like Pages */}
      <div className="flex-1 flex flex-col">
        {/* Document Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {sections.length} sections • Continuous scroll view
          </div>
          <div className="text-sm text-gray-600">{documentTitle}</div>
        </div>

        {/* PDF-like Page Content - Enhanced scrollable area */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-4" style={{ maxHeight: 'calc(100vh - 120px)' }}>
          <div className="max-w-4xl mx-auto">
            {/* PDF Page Container */}
            <div
              className="bg-white shadow-lg rounded-lg overflow-hidden"
              style={{ minHeight: "11in" }}
            >
              {/* Page Header with Branding */}
              <div className="border-b border-gray-200 p-6 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {logoUrl && (
                      <img
                        src={logoUrl}
                        alt={brandName}
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          console.error("Logo failed to load:", logoUrl);
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    )}
                    <div>
                      <h1
                        className="text-xl font-bold text-gray-900"
                        style={{ color: primaryColor }}
                      >
                        {brandName}
                      </h1>
                      {organizationBranding?.tagline && (
                        <p className="text-sm text-gray-600">
                          {organizationBranding.tagline}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {documentTitle}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Prepared for {clientData.company}
                    </p>
                  </div>
                </div>
              </div>



              {/* Continuous Content - All Sections */}
              <div className="p-8">
                {sections.map((section, index) => (
                  <div
                    key={section.id}
                    className={`mb-8 ${
                      selectedSectionId === section.id
                        ? "bg-blue-50 border-l-4 border-blue-500 pl-4"
                        : ""
                    }`}
                    ref={(el) => {
                      if (el) sectionRefs.current[section.id] = el;
                    }}
                  >
                    {/* Hide section title for cover page, show for other sections */}
                    {section.id !== 'cover-page' && (
                      <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200">
                        <h3
                          className="text-xl font-semibold"
                          style={{ color: primaryColor }}
                        >
                          {index + 1}. {section.title}
                        </h3>
                        {section.regenerated && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Regenerated
                          </span>
                        )}
                        {section.fallback && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            ⚠ Fallback
                          </span>
                        )}
                      </div>
                    )}
                    <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700">
                      {(() => {
                        try {
                          // 🚨 SANITIZE AND VALIDATE CONTENT BEFORE RENDERING
                          let sanitizedContent = section.content || '';

                          // Remove any problematic characters or patterns
                          sanitizedContent = sanitizedContent
                            .replace(/\r\n/g, '\n') // Normalize line endings
                            .replace(/\r/g, '\n')   // Handle old Mac line endings
                            .trim(); // Remove leading/trailing whitespace

                          // Check if content is valid markdown
                          if (!sanitizedContent) {
                            return (
                              <div className="text-gray-500 italic p-4 bg-gray-50 rounded">
                                No content available for this section.
                              </div>
                            );
                          }

                          // Log problematic sections for debugging
                          if (sanitizedContent.includes('```') && !sanitizedContent.match(/```[\s\S]*?```/)) {
                            console.warn(`🚨 Potentially malformed code block in section: ${section.title}`);
                          }

                          return (
                            <PerfectMarkdownRenderer
                              content={sanitizedContent}
                              className="prose-lg"
                            />
                          );
                        } catch (error) {
                          // 🚨 FALLBACK: Show raw content if ReactMarkdown fails
                          console.error(`❌ ReactMarkdown failed for section "${section.title}":`, error);
                          return (
                            <div className="border border-red-200 bg-red-50 p-4 rounded-lg">
                              <div className="text-red-600 font-medium mb-2">
                                ⚠️ Markdown rendering failed for this section
                              </div>
                              <div className="text-sm text-gray-600 mb-3">
                                Showing raw content instead:
                              </div>
                              <pre className="whitespace-pre-wrap text-sm bg-white p-3 rounded border overflow-x-auto">
                                {section.content}
                              </pre>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Document Footer */}
              <div className="border-t border-gray-200 p-4 bg-gray-50 mt-auto">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div>
                    {clientData.company} • {projectData.title}
                  </div>
                  <div>{sections.length} sections • Continuous view</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PDFLikePreview.displayName = 'PDFLikePreview';

export default PDFLikePreview;
